/**
 * TreatX Clinic Data Context - Phase 2B Cleanup
 * 
 * PHASE 2A CHANGES:
 * - Removed hardcoded clinicAccounts / loginClinic (use unified /api/auth/unified/login)
 * - Removed static salesReps and doctors imports (use provider_affiliations, /api/sales-reps)
 * PHASE 2B CHANGES:
 * - clinicSettings now fetched from/saved to backend API (/api/clinic-settings/:clinicId)
 * - Removed sessionStorage for clinicSettings
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import axios from 'axios';
import StorageService, { STORAGE_KEYS } from '../services/StorageService';
// SPRINT 6B-02 & 6B-04: Lead State Machine
import {
  isValidTransition,
  getValidNextStatuses,
  canRoleTransition,
  getAvailableTransitionsForRole,
  getResponsibleRole,
  getPhaseForStatus,
  isHandoffTransition,
  getTransitionDescription,
  VALID_TRANSITIONS,
  TRANSITION_PERMISSIONS
} from '../utils/leadStateMachine';
import api from '../utils/api';
import { useRBAC } from './RBACContext';

const ClinicDataContext = createContext();

export const useClinicData = () => {
  const context = useContext(ClinicDataContext);
  if (!context) {
    throw new Error('useClinicData must be used within ClinicDataProvider');
  }
  return context;
};

// =============================================================================
// SPRINT 6B-01: Lead Status Pipeline - Complete 10-state lifecycle
// =============================================================================
export const LEAD_STATUSES = {
  // Pre-Sales Phase
  NEW: { 
    value: 'NEW', 
    label: 'New', 
    color: 'bg-blue-100 text-blue-800', 
    description: 'Newly received lead, awaiting assignment',
    phase: 'PRE_SALES',
    terminal: false
  },
  ASSIGNED: { 
    value: 'ASSIGNED', 
    label: 'Assigned', 
    color: 'bg-indigo-100 text-indigo-800', 
    description: 'Assigned to sales rep',
    phase: 'PRE_SALES',
    terminal: false
  },
  CONTACTED: { 
    value: 'CONTACTED', 
    label: 'Contacted', 
    color: 'bg-yellow-100 text-yellow-800', 
    description: 'First meaningful patient contact made',
    phase: 'PRE_SALES',
    terminal: false
  },
  // Negotiation Phase
  OFFER_SENT: { 
    value: 'OFFER_SENT', 
    label: 'Offer Sent', 
    color: 'bg-purple-100 text-purple-800', 
    description: 'Offer sent to patient, awaiting response',
    phase: 'NEGOTIATION',
    terminal: false
  },
  // QOF-02: Patient has accepted the offer — order materialised. Was
  // previously absent from this map, causing the SalesRepDashboard's
  // `LEAD_STATUSES[lead.status] || LEAD_STATUSES.NEW` fallback to display
  // these leads as "New", masking the real lifecycle from the rep.
  ORDER_CREATED: {
    value: 'ORDER_CREATED',
    label: 'Order Created',
    color: 'bg-violet-100 text-violet-800',
    description: 'Patient accepted the offer — order created, awaiting deposit',
    phase: 'NEGOTIATION',
    terminal: false
  },
  WON: {
    value: 'WON',
    label: 'Won',
    color: 'bg-emerald-100 text-emerald-800',
    description: 'Lead converted to a committed order',
    phase: 'COMMITTED',
    terminal: false
  },
  QUALIFIED: {
    value: 'QUALIFIED',
    label: 'Qualified',
    color: 'bg-amber-100 text-amber-800',
    description: 'Lead qualified by clinic, ready for offer',
    phase: 'PRE_SALES',
    terminal: false
  },
  NEGOTIATION: {
    value: 'NEGOTIATION',
    label: 'Negotiating',
    color: 'bg-purple-100 text-purple-800',
    description: 'Negotiating offer terms with patient',
    phase: 'NEGOTIATION',
    terminal: false
  },
  ON_HOLD: {
    value: 'ON_HOLD',
    label: 'On Hold',
    color: 'bg-slate-100 text-slate-700',
    description: 'Lead paused — awaiting external input',
    phase: 'PRE_SALES',
    terminal: false
  },
  QUOTE_SENT: {
    value: 'QUOTE_SENT',
    label: 'Quote Sent',
    color: 'bg-purple-100 text-purple-800',
    description: 'Legacy alias for OFFER_SENT',
    phase: 'NEGOTIATION',
    terminal: false,
    deprecated: true
  },
  // Committed Phase
  DEPOSIT_PAID: { 
    value: 'DEPOSIT_PAID', 
    label: 'Deposit Paid', 
    color: 'bg-green-100 text-green-800', 
    description: 'Deposit confirmed, patient committed',
    phase: 'COMMITTED',
    terminal: false
  },
  // Clinical Phase
  IN_TREATMENT: { 
    value: 'IN_TREATMENT', 
    label: 'In Treatment', 
    color: 'bg-teal-100 text-teal-800', 
    description: 'Patient arrived, treatment in progress',
    phase: 'CLINICAL',
    terminal: false
  },
  POST_OP: { 
    value: 'POST_OP', 
    label: 'Post-Op', 
    color: 'bg-cyan-100 text-cyan-800', 
    description: 'Treatment complete, in recovery/follow-up',
    phase: 'CLINICAL',
    terminal: false
  },
  // Terminal States
  CLOSED_WON: { 
    value: 'CLOSED_WON', 
    label: 'Closed Won', 
    color: 'bg-emerald-100 text-emerald-800', 
    description: 'Treatment completed successfully',
    phase: 'TERMINAL',
    terminal: true
  },
  CLOSED_LOST: { 
    value: 'CLOSED_LOST', 
    label: 'Closed Lost', 
    color: 'bg-red-100 text-red-800', 
    description: 'Patient declined, competitor won, or unresponsive',
    phase: 'TERMINAL',
    terminal: true
  },
  CLOSED_CANCELLED: { 
    value: 'CLOSED_CANCELLED', 
    label: 'Cancelled', 
    color: 'bg-gray-100 text-gray-800', 
    description: 'Cancelled by patient or clinic',
    phase: 'TERMINAL',
    terminal: true
  },
  // Legacy alias for backward compatibility
  LOST: { 
    value: 'CLOSED_LOST', 
    label: 'Lost', 
    color: 'bg-red-100 text-red-800', 
    description: 'Lead did not convert',
    phase: 'TERMINAL',
    terminal: true,
    deprecated: true
  }
};

// Status groups for filtering and display
export const LEAD_STATUS_GROUPS = {
  PRE_SALES: ['NEW', 'ASSIGNED', 'CONTACTED', 'QUALIFIED', 'ON_HOLD'],
  NEGOTIATION: ['OFFER_SENT', 'QUOTE_SENT', 'NEGOTIATION', 'ORDER_CREATED'],
  COMMITTED: ['DEPOSIT_PAID', 'WON'],
  CLINICAL: ['IN_TREATMENT', 'POST_OP'],
  TERMINAL: ['CLOSED_WON', 'CLOSED_LOST', 'CLOSED_CANCELLED'],
  ACTIVE: ['NEW', 'ASSIGNED', 'CONTACTED', 'QUALIFIED', 'OFFER_SENT', 'QUOTE_SENT', 'NEGOTIATION', 'ORDER_CREATED', 'DEPOSIT_PAID', 'WON', 'IN_TREATMENT', 'POST_OP'],
  CLOSED: ['CLOSED_WON', 'CLOSED_LOST', 'CLOSED_CANCELLED']
};

// Helper to check if a status is terminal (no further transitions allowed)
export const isTerminalStatus = (status) => {
  return LEAD_STATUSES[status]?.terminal === true;
};

// Helper to check if a status is closed
export const isClosedStatus = (status) => {
  return LEAD_STATUS_GROUPS.CLOSED.includes(status);
};

// =============================================================================
// SPRINT 8 - BATCH 3: Lead Immutability for CLOSED Leads
// Once a lead reaches a terminal state, it becomes immutable
// =============================================================================

/**
 * Check if a lead is immutable (in a CLOSED/terminal state)
 * Immutable leads cannot have their core data modified
 * @param {object} lead - Lead object
 * @returns {boolean} True if lead is immutable
 */
export const isLeadImmutable = (lead) => {
  if (!lead) return false;
  return LEAD_STATUS_GROUPS.CLOSED.includes(lead.status);
};

/**
 * Get the reason why a lead is immutable
 * @param {object} lead - Lead object
 * @returns {string|null} Reason string or null if not immutable
 */
export const getImmutabilityReason = (lead) => {
  if (!lead) return null;
  if (!isLeadImmutable(lead)) return null;
  
  const statusInfo = LEAD_STATUSES[lead.status];
  return `This lead is ${statusInfo?.label || 'closed'} and cannot be modified. Closed leads are locked for data integrity.`;
};

/**
 * List of allowed operations on immutable leads
 * - Viewing is always allowed
 * - Adding audit notes is allowed (append-only)
 * - System metadata updates are allowed (viewedAt, etc.)
 */
export const IMMUTABLE_LEAD_ALLOWED_OPERATIONS = [
  'VIEW',
  'ADD_AUDIT_NOTE',
  'UPDATE_METADATA'
];

// =============================================================================
// SPRINT 6B-19: Lead Creation Source - Who created the lead
// =============================================================================
export const LEAD_CREATION_SOURCE = {
  PLATFORM: {
    value: 'PLATFORM',
    label: 'Platform (Patient)',
    description: 'Lead created via patient self-registration on TreatX marketplace',
    initialStatus: 'NEW',
    autoAssign: false
  },
  CLINIC_ADMIN: {
    value: 'CLINIC_ADMIN',
    label: 'Clinic Admin',
    description: 'Lead manually entered by clinic administrator',
    initialStatus: 'NEW',
    autoAssign: false
  },
  SALES_REP: {
    value: 'SALES_REP',
    label: 'Sales',
    description: 'Lead created by sales representative (self-owned)',
    initialStatus: 'ASSIGNED',
    autoAssign: true
  }
};

// Helper to get initial status based on creation source
export const getInitialStatusBySource = (creationSource) => {
  return LEAD_CREATION_SOURCE[creationSource]?.initialStatus || 'NEW';
};

// Lead Source Types - NEW: Distinguish marketplace vs provider-owned leads
export const LEAD_SOURCE_TYPES = {
  TREATX_MARKETPLACE: { 
    value: 'TREATX_MARKETPLACE', 
    label: 'TreatX Marketplace', 
    color: 'bg-purple-100 text-purple-700',
    icon: 'globe',
    commissionEligible: true 
  },
  PROVIDER_OWNED: { 
    value: 'PROVIDER_OWNED', 
    label: 'Clinic-Owned', 
    color: 'bg-amber-100 text-amber-700',
    icon: 'building',
    commissionEligible: false 
  }
};

// Lead Source Channels - For provider-owned leads
export const LEAD_SOURCE_CHANNELS = {
  SELF_SOURCED: { value: 'SELF_SOURCED', label: 'Self-Sourced', icon: 'user' },
  REFERRAL: { value: 'REFERRAL', label: 'Referral', icon: 'users' },
  INSTAGRAM: { value: 'INSTAGRAM', label: 'Instagram', icon: 'instagram' },
  WHATSAPP: { value: 'WHATSAPP', label: 'WhatsApp', icon: 'message-circle' },
  WEBSITE: { value: 'WEBSITE', label: 'Website', icon: 'globe' },
  FACEBOOK: { value: 'FACEBOOK', label: 'Facebook', icon: 'facebook' },
  PHONE_CALL: { value: 'PHONE_CALL', label: 'Phone Call', icon: 'phone' },
  WALK_IN: { value: 'WALK_IN', label: 'Walk-In', icon: 'footprints' },
  OTHER: { value: 'OTHER', label: 'Other', icon: 'more-horizontal' }
};

// clinicAccounts REMOVED in Phase 2A — all auth via /api/auth/unified/login

// Demo leads removed — all lead data now comes from backend API via useClinicLeads hook.

/**
 * Initialize leads state - leads come from backend API.
 * Returns empty array. Components should use useClinicLeads hook.
 */
const getInitialLeads = () => {
  return [];
};

/**
 * Initialize patients state - patients come from backend API.
 * Returns empty array.
 */
const getInitialPatients = () => {
  return [];
};

/**
 * Initialize offers state - offers now come from backend API.
 * Returns empty array. Components should use useClinicLeads hook for offer operations.
 */
const getInitialOffers = () => {
  return [];
};

// Clinic profiles for editing
// P2.2: Uses StorageService.getSession
const getInitialClinicProfiles = () => {
  if (typeof window !== 'undefined') {
    const saved = StorageService.getSession(STORAGE_KEYS.CLINIC_PROFILES);
    if (saved) {
      return saved;
    }
  }
  return {};
};

export const ClinicDataProvider = ({ children }) => {
  // OF-001E — clinicUser is DERIVED from RBACContext's activeWorkspace
  // + persisted unified-user metadata.  Single SSOT, no second /me
  // fetcher, no 500ms polling, no hashchange listener.
  //
  // Before OF-001E this provider ran a SECOND `GET /auth/unified/me`
  // independently of RBACContext (driven by a `hashchange`-trigger
  // useEffect), polled sessionStorage every 500ms while clinicUser
  // was null, and listened to `storage`/`hashchange` events to
  // re-pull from sessionStorage.  Four concurrent writers raced
  // every workspace switch — the documented "first click blank"
  // symptom.
  //
  // Post-fix flow:
  //   - RBACContext owns the authoritative profile (`/auth/unified/me`).
  //   - clinicUser is a `useMemo` of (activeWorkspace, persisted user).
  //   - sessionStorage write is a side-effect, NOT a source of truth.
  //   - manual hydrate path retained for explicit login flows.
  const rbac = useRBAC();
  const activeWorkspace = rbac?.activeWorkspace || null;

  // Persisted "primary" user metadata (id/email/name/doctorId).  Set
  // once at login; survives reloads via localStorage.
  const readUserMeta = () => {
    try {
      const raw = localStorage.getItem('treatx_unified_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const [userMeta, setUserMeta] = useState(readUserMeta);
  const [doctorId, setDoctorId] = useState(() => {
    try {
      const cu = StorageService.getSession(STORAGE_KEYS.CLINIC_USER);
      return cu?.doctorId || null;
    } catch { return null; }
  });

  // Surfaces that mutate `treatx_unified_user` (login, social auth,
  // logout) dispatch `treatx:identity-changed` already; we listen
  // there to re-read the metadata.
  useEffect(() => {
    const onIdent = () => setUserMeta(readUserMeta());
    window.addEventListener('treatx:identity-changed', onIdent);
    return () => window.removeEventListener('treatx:identity-changed', onIdent);
  }, []);

  // Derived clinicUser.  Updates atomically with `activeWorkspace`
  // (RBACContext is the single writer).
  const clinicUser = useMemo(() => {
    if (!userMeta) return null;
    if (!activeWorkspace || activeWorkspace.type !== 'provider' || !activeWorkspace.clinic_id) {
      // Patient or unbound workspace — clinicUser is not applicable.
      return null;
    }
    const u = {
      id: userMeta.id || userMeta.user_id,
      userId: userMeta.id || userMeta.user_id,
      email: userMeta.email,
      name: userMeta.name,
      role: activeWorkspace.role_id,
      clinicId: activeWorkspace.clinic_id,
      clinicName: activeWorkspace.clinic_name || userMeta.clinic_name || '',
      avatar: '',
      ...(doctorId ? { doctorId } : {}),
    };
    // Mirror to sessionStorage for legacy consumers that still read it
    // directly (filenames documented in OF-001E active_surface_matrix).
    try {
      StorageService.setSession(STORAGE_KEYS.CLINIC_USER, u);
    } catch { /* ignore */ }
    return u;
  }, [userMeta, activeWorkspace, doctorId]);

  // Explicit hydration handle for the login page (back-compat).
  const hydrateClinicUser = useCallback((userData) => {
    if (!userData) return;
    if (userData.doctorId) setDoctorId(userData.doctorId);
    // Backfill userMeta so the memo picks the latest at next render.
    setUserMeta((prev) => ({
      ...(prev || {}),
      id: userData.id || userData.userId,
      email: userData.email,
      name: userData.name,
      clinic_name: userData.clinicName,
    }));
    try {
      StorageService.setSession(STORAGE_KEYS.CLINIC_USER, userData);
    } catch { /* ignore */ }
  }, []);

  // setClinicUser is preserved as a no-op for code paths that import
  // it but should be derived now.  Real updates flow through
  // `hydrateClinicUser` or the activeWorkspace memo above.
  const setClinicUser = useCallback((u) => {
    if (u?.doctorId && u.doctorId !== doctorId) setDoctorId(u.doctorId);
    if (u) {
      try { StorageService.setSession(STORAGE_KEYS.CLINIC_USER, u); } catch { /* ignore */ }
    }
  }, [doctorId]);

  // Targeted enrichment: doctors need `doctorId` from `/me` to power
  // the doctor dashboard.  We fetch ONCE per session when an active
  // provider_doctor workspace is selected and `doctorId` is missing.
  // Does NOT run on workspace switch for non-doctor roles — those
  // need zero network traffic to render their dashboard.
  useEffect(() => {
    if (doctorId) return;
    if (!activeWorkspace || activeWorkspace.role_id !== 'provider_doctor') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/unified/me');
        if (cancelled) return;
        if (res.status >= 200 && res.status < 300 && res.data?.doctor_id) {
          setDoctorId(res.data.doctor_id);
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.error('[ClinicData] doctorId enrichment failed:', err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeWorkspace, doctorId]);
  
  // P0 FIX: clinicAccounts state REMOVED — auth via unified API, reps/doctors via /api hooks
  
  // SPRINT 8: Separate patients collection
  const [patients, setPatients] = useState(getInitialPatients);
  const [leads, setLeads] = useState(getInitialLeads);
  const [offers, setOffers] = useState(getInitialOffers);
  const [clinicProfiles, setClinicProfiles] = useState(getInitialClinicProfiles);
  const [distributionMode, setDistributionMode] = useState('manual'); // manual, round-robin, specialization
  
  // Item 7: Clinic settings for offer approval workflow
  // Phase 2B: Now backed by /api/clinic-settings/:clinicId
  const [clinicSettings, setClinicSettings] = useState({});

  // Load clinic settings from API when clinicUser changes
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const loadClinicSettings = useCallback(async (clinicId) => {
    if (!clinicId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/clinic-settings/${clinicId}`);
      if (data.success && data.settings) {
        const s = data.settings;
        setClinicSettings(prev => ({
          ...prev,
          [clinicId]: {
            requireOfferApproval: s.require_offer_approval || false,
            approvalThreshold: s.approval_threshold || 0,
            responseSlaHours: s.response_sla_hours || 24,
            autoAssignLeads: s.auto_assign_leads || false,
            distributionMode: s.distribution_mode || 'manual',
          }
        }));
      }
    } catch (err) {
      console.error('[ClinicData] Failed to load clinic settings:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    if (clinicUser?.clinicId) {
      loadClinicSettings(clinicUser.clinicId);
    }
  }, [clinicUser, loadClinicSettings]);

  // loginClinic REMOVED — all auth via /api/auth/unified/login (UnifiedLoginPage)

  // Persist to sessionStorage
  // SPRINT 8: Persist patients collection
  // P2.2: Uses StorageService.setSession
  useEffect(() => {
    StorageService.setSession(STORAGE_KEYS.PATIENTS, patients);
  }, [patients]);

  // Leads persistence REMOVED: Leads are now backend-driven via useClinicLeads hook
  // Offers persistence REMOVED: Offers are now backend-driven via useClinicLeads hook

  // P2.2: Uses StorageService.setSession
  useEffect(() => {
    StorageService.setSession(STORAGE_KEYS.CLINIC_PROFILES, clinicProfiles);
  }, [clinicProfiles]);
  
  // clinicSettings persistence: now handled by backend API (see loadClinicSettings)

  // P2.2: persistence — sessionStorage mirror is handled inside the
  // useMemo for clinicUser (write-on-derive).  This effect only
  // handles the logout clear path.
  useEffect(() => {
    if (!clinicUser) {
      try { StorageService.removeSession(STORAGE_KEYS.CLINIC_USER); } catch { /* ignore */ }
    }
  }, [clinicUser]);

  // loginClinic function REMOVED — use UnifiedLoginPage / /api/auth/unified/login

  /**
   * Create and auto-login a new clinic user (for post-approval flow)
   * This is used when a clinic starts a trial or selects a paid plan
   * P2.2: Uses StorageService.setSession
   */
  const createAndLoginNewClinic = (clinicInfo) => {
    // Use provided clinicId or generate one
    const clinicId = clinicInfo.clinicId || `clinic-${Date.now()}`;
    
    const newClinicUser = {
      id: `user-${Date.now()}`,
      email: clinicInfo.email || '',
      name: clinicInfo.clinicName || clinicInfo.name || 'New Clinic Admin',
      role: 'provider_admin',
      clinicId: clinicId,
      clinicName: clinicInfo.clinicName || 'My New Clinic',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&q=80',
      isNewClinic: true, // Flag to indicate this is a newly registered clinic
    };
    
    setClinicUser(newClinicUser);
    StorageService.setSession(STORAGE_KEYS.CLINIC_USER, newClinicUser);
    return { success: true, user: newClinicUser, clinicId: clinicId };
  };

  // P2.2: Uses StorageService.removeSession
  const logoutClinic = () => {
    setClinicUser(null);
    StorageService.removeSession(STORAGE_KEYS.CLINIC_USER);
  };

  // getSalesRepsForClinic REMOVED — use useSalesReps hook with /api/sales-reps

  // addSalesRep REMOVED — use useSalesReps hook with POST /api/sales-reps

  // Lead management functions
  const getLeadsForClinic = (clinicId) => {
    return leads.filter(lead => lead.clinicId === clinicId);
  };

  const getLeadsForRep = (repId) => {
    // P0-B FIX: Support both camelCase (context data) and snake_case (API data)
    return leads.filter(lead => 
      lead.assignedRepId === repId || lead.assigned_agent_id === repId
    );
  };

  const getLeadById = (leadId) => {
    return leads.find(lead => lead.id === leadId);
  };

  // =============================================================================
  // SPRINT 8: Patient Management Functions
  // =============================================================================
  
  /**
   * Get patient by ID
   * @param {string} patientId - Patient ID
   * @returns {object|null} Patient object or null if not found
   */
  const getPatientById = (patientId) => {
    return patients.find(p => p.id === patientId) || null;
  };

  /**
   * Get patient by email (case-insensitive)
   * @param {string} email - Patient email
   * @returns {object|null} Patient object or null if not found
   */
  const getPatientByEmail = (email) => {
    if (!email) return null;
    return patients.find(p => p.email.toLowerCase().trim() === email.toLowerCase().trim()) || null;
  };

  /**
   * Create or get existing patient (unique by email)
   * @param {object} patientData - Patient data { name, email, phone, country }
   * @returns {object} Patient object (existing or newly created)
   */
  const getOrCreatePatient = (patientData) => {
    const email = (patientData.email || '').toLowerCase().trim();
    
    // Check if patient already exists
    const existing = getPatientByEmail(email);
    if (existing) {
      return existing;
    }
    
    // Create new patient
    const newPatient = {
      id: `patient-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: patientData.name || 'Unknown',
      email: patientData.email || '',
      phone: patientData.phone || '',
      country: patientData.country || 'Unknown',
      createdAt: new Date().toISOString()
    };
    
    setPatients(prev => [...prev, newPatient]);
    return newPatient;
  };

  /**
   * Update patient information
   * @param {string} patientId - Patient ID
   * @param {object} updates - Fields to update
   */
  const updatePatient = (patientId, updates) => {
    setPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return { ...p, ...updates, updatedAt: new Date().toISOString() };
      }
      return p;
    }));
  };

  /**
   * Get all leads for a specific patient
   * @param {string} patientId - Patient ID
   * @returns {array} Array of leads for this patient
   */
  const getLeadsForPatient = (patientId) => {
    return leads.filter(l => l.patientId === patientId);
  };

  // =============================================================================

  // SPRINT 8 BATCH 3: Immutability guard wrapper
  const checkLeadImmutability = (leadId, operation = 'modify') => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return { allowed: true }; // Lead not found, let other logic handle
    
    if (isLeadImmutable(lead)) {
      return {
        allowed: false,
        reason: getImmutabilityReason(lead),
        leadStatus: lead.status
      };
    }
    return { allowed: true };
  };
  
  // =============================================================================
  // SPRINT 9 Batch 3.1: Activity Logging
  // =============================================================================
  
  /**
   * Create an activity log entry
   * @param {string} type - Activity type (e.g., 'STATUS_CHANGED', 'ASSIGNED_TO_REP')
   * @param {object} details - Activity details
   * @returns {object} Activity log entry
   */
  const createActivityEntry = (type, details = {}) => {
    return {
      id: `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      timestamp: new Date().toISOString(),
      title: details.title || type,
      description: details.description || null,
      actor: details.actor || clinicUser?.name || 'System',
      actorId: details.actorId || clinicUser?.id || null,
      metadata: details.metadata || {}
    };
  };
  
  /**
   * Add an activity to a lead's activity log
   * @param {string} leadId - Lead ID
   * @param {string} type - Activity type
   * @param {object} details - Activity details
   */
  const addActivityToLead = (leadId, type, details = {}) => {
    const activity = createActivityEntry(type, details);
    setLeads(prev => prev.map(lead => {
      if (lead.id === leadId) {
        return {
          ...lead,
          activityLog: [...(lead.activityLog || []), activity]
        };
      }
      return lead;
    }));
    return activity;
  };


  // Clinic profile management
  const updateClinicProfile = async (clinicId, profileData) => {
    // Update local state immediately
    setClinicProfiles(prev => ({
      ...prev,
      [clinicId]: { ...prev[clinicId], ...profileData, updatedAt: new Date().toISOString() }
    }));
    // Persist to backend
    try {
      await axios.post(`${API_URL}/api/clinic/profile`, {
        clinic_id: clinicId,
        ...profileData,
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
    } catch (err) {
      console.error('[ClinicData] Failed to save clinic profile to backend:', err);
    }
  };

  const getClinicProfile = (clinicId) => {
    return clinicProfiles[clinicId] || null;
  };

  // Clinic settings management
  const getClinicSettings = (clinicId) => {
    return clinicSettings[clinicId] || { requireOfferApproval: false, approvalThreshold: 0 };
  };

  const updateClinicSettings = async (clinicId, settings) => {
    // Save to backend
    try {
      await axios.put(`${API_URL}/api/clinic-settings/${clinicId}`, {
        require_offer_approval: settings.requireOfferApproval,
        approval_threshold: settings.approvalThreshold,
        response_sla_hours: settings.responseSlaHours,
        auto_assign_leads: settings.autoAssignLeads,
        distribution_mode: settings.distributionMode,
      });
    } catch (err) {
      console.error('[ClinicData] Failed to save clinic settings:', err);
    }
    // Optimistic update
    setClinicSettings(prev => ({
      ...prev,
      [clinicId]: { ...prev[clinicId], ...settings }
    }));
  };

  // Stats functions
  const getClinicStats = (clinicId) => {
    const clinicLeads = getLeadsForClinic(clinicId);
    return {
      total: clinicLeads.length,
      new: clinicLeads.filter(l => l.status === 'NEW').length,
      assigned: clinicLeads.filter(l => l.status === 'ASSIGNED').length,
      contacted: clinicLeads.filter(l => l.status === 'CONTACTED').length,
      offerSent: clinicLeads.filter(l => l.status === 'OFFER_SENT').length,
      depositPaid: clinicLeads.filter(l => l.status === 'DEPOSIT_PAID').length,
      closedWon: clinicLeads.filter(l => l.status === 'CLOSED_WON').length,
      lost: clinicLeads.filter(l => l.status === 'LOST').length
    };
  };

  const getRepStats = (repId) => {
    const repLeads = getLeadsForRep(repId);
    const repOffers = offers.filter(o => {
      const lead = getLeadById(o.leadId);
      return lead?.assignedRepId === repId;
    });
    const closedWon = repLeads.filter(l => l.status === 'CLOSED_WON').length;
    const total = repLeads.length;
    
    return {
      totalLeads: total,
      activeLeads: repLeads.filter(l => !['CLOSED_WON', 'LOST'].includes(l.status)).length,
      offersSent: repOffers.length,
      conversionRate: total > 0 ? Math.round((closedWon / total) * 100) : 0,
      // P0.1 FIX: Use UPPER_CASE keys to match LEAD_STATUSES for proper label lookup
      byStatus: {
        NEW: repLeads.filter(l => l.status === 'NEW').length,
        ASSIGNED: repLeads.filter(l => l.status === 'ASSIGNED').length,
        CONTACTED: repLeads.filter(l => l.status === 'CONTACTED').length,
        OFFER_SENT: repLeads.filter(l => l.status === 'OFFER_SENT').length,
        DEPOSIT_PAID: repLeads.filter(l => l.status === 'DEPOSIT_PAID').length,
        IN_TREATMENT: repLeads.filter(l => l.status === 'IN_TREATMENT').length,
        POST_OP: repLeads.filter(l => l.status === 'POST_OP').length,
        CLOSED_WON: closedWon,
        LOST: repLeads.filter(l => l.status === 'LOST').length
      }
    };
  };

  // Bulk reassign leads from one sales rep to another
  const bulkReassignLeads = (fromRepId, toRepId, clinicId, toRepName) => {
    if (!toRepName) return 0;

    let reassignedCount = 0;
    setLeads(prev => prev.map(lead => {
      if (lead.assignedRepId === fromRepId && 
          lead.clinicId === clinicId &&
          !['CLOSED_WON', 'LOST'].includes(lead.status)) {
        reassignedCount++;
        return {
          ...lead,
          assignedRepId: toRepId,
          assignedRepName: toRepName,
          notes: [...(lead.notes || []), `Lead reassigned to ${toRepName} on ${formatDateLocale(new Date())}`]
        };
      }
      return lead;
    }));

    return reassignedCount;
  };

  // getDoctorsForClinic REMOVED — use provider_affiliations via /api/providers/{id}/doctors

  // Get patients/leads assigned to a doctor (via DEPOSIT_PAID or CLOSED_WON status)
  const getPatientsForDoctor = (doctorId) => {
    return leads.filter(lead => 
      lead.assignedDoctorId === doctorId && 
      ['DEPOSIT_PAID', 'CLOSED_WON'].includes(lead.status)
    );
  };

  // Assign a doctor to a lead/patient
  const assignDoctorToLead = (leadId, doctorId, doctorName) => {
    // SPRINT 9 Batch 2.2: Check immutability
    const immutabilityCheck = checkLeadImmutability(leadId, 'assign doctor');
    if (!immutabilityCheck.allowed) {
      console.warn(`[Immutability] Cannot assign doctor to closed lead ${leadId}: ${immutabilityCheck.reason}`);
      return { success: false, error: immutabilityCheck.reason };
    }
    
    setLeads(prev => prev.map(lead => {
      if (lead.id === leadId) {
        return {
          ...lead,
          assignedDoctorId: doctorId,
          assignedDoctorName: doctorName || 'Unassigned'
        };
      }
      return lead;
    }));
    return { success: true };
  };

  // Bulk reassign patients from one doctor to another
  const bulkReassignPatients = (fromDoctorId, toDoctorId, clinicId, toDoctorName) => {
    if (!toDoctorName) return 0;

    let reassignedCount = 0;
    setLeads(prev => prev.map(lead => {
      if (lead.assignedDoctorId === fromDoctorId && 
          lead.clinicId === clinicId &&
          lead.status === 'DEPOSIT_PAID') {
        reassignedCount++;
        return {
          ...lead,
          assignedDoctorId: toDoctorId,
          assignedDoctorName: toDoctorName,
          notes: [...(lead.notes || []), `Patient reassigned to ${toDoctorName} on ${formatDateLocale(new Date())}`]
        };
      }
      return lead;
    }));

    return reassignedCount;
  };

  // Mark a sales rep as inactive (leaving)
  // P2.2: Uses StorageService.getSession/setSession
  const markSalesRepAsInactive = (repId) => {
    const inactiveReps = StorageService.getSession(STORAGE_KEYS.INACTIVE_SALES_REPS) || [];
    if (!inactiveReps.includes(repId)) {
      inactiveReps.push(repId);
      StorageService.setSession(STORAGE_KEYS.INACTIVE_SALES_REPS, inactiveReps);
    }
  };

  // Check if a sales rep is inactive
  // P2.2: Uses StorageService.getSession
  const isSalesRepInactive = (repId) => {
    const inactiveReps = StorageService.getSession(STORAGE_KEYS.INACTIVE_SALES_REPS) || [];
    return inactiveReps.includes(repId);
  };

  // Mark a doctor as inactive (leaving)
  // P2.2: Uses StorageService.getSession/setSession
  const markDoctorAsInactive = (doctorId) => {
    const inactiveDoctors = StorageService.getSession(STORAGE_KEYS.INACTIVE_DOCTORS) || [];
    if (!inactiveDoctors.includes(doctorId)) {
      inactiveDoctors.push(doctorId);
      StorageService.setSession(STORAGE_KEYS.INACTIVE_DOCTORS, inactiveDoctors);
    }
  };

  // Check if a doctor is inactive
  // P2.2: Uses StorageService.getSession
  const isDoctorInactive = (doctorId) => {
    const inactiveDoctors = StorageService.getSession(STORAGE_KEYS.INACTIVE_DOCTORS) || [];
    return inactiveDoctors.includes(doctorId);
  };

  const value = {
    // Auth (clinicUser read from sessionStorage, set by UnifiedLoginPage)
    clinicUser,
    hydrateClinicUser,
    createAndLoginNewClinic,
    logoutClinic,
    isClinicAdmin: clinicUser?.role === 'provider_admin' || clinicUser?.role === 'treatx_admin',
    isSalesRep: clinicUser?.role === 'provider_sales_rep',
    isDoctor: clinicUser?.role === 'provider_doctor',
    
    // SPRINT 8: Patient Management
    patients,
    getPatientById,
    getPatientByEmail,
    getOrCreatePatient,
    updatePatient,
    getLeadsForPatient,
    
    // Leads (read-only from context — mutations via useClinicLeads hook)
    leads,
    getLeadsForClinic,
    getLeadsForRep,
    getLeadById,
    addActivityToLead, // SPRINT 9 Batch 3.1: Activity logging
    
    // Bulk Reassignment
    bulkReassignLeads,
    bulkReassignPatients,
    
    // Sales Rep Management
    markSalesRepAsInactive,
    isSalesRepInactive,
    
    // Doctor Management (getDoctorsForClinic REMOVED — use provider_affiliations)
    getPatientsForDoctor,
    assignDoctorToLead,
    markDoctorAsInactive,
    isDoctorInactive,
    
    // Offers (read-only from context — mutations via useClinicLeads hook)
    offers,
    
    // Clinic Settings (Item 7)
    getClinicSettings,
    updateClinicSettings,
    
    // Clinic Profile
    updateClinicProfile,
    getClinicProfile,
    
    // Stats
    getClinicStats,
    getRepStats,
    
    // Helpers (getSalesRepsForClinic / addSalesRep REMOVED — use useSalesReps hook)
    // SPRINT 6B-01: Complete lead status definitions
    LEAD_STATUSES,
    LEAD_STATUS_GROUPS,
    isTerminalStatus,
    isClosedStatus,
    // SPRINT 8 BATCH 3: Lead Immutability
    isLeadImmutable,
    getImmutabilityReason,
    IMMUTABLE_LEAD_ALLOWED_OPERATIONS,
    // SPRINT 6B-19: Lead creation source tracking
    LEAD_CREATION_SOURCE,
    getInitialStatusBySource,
    // SPRINT 6B-02 & 6B-04: Lead State Machine & Role Permissions (read-only helpers)
    isValidTransition,
    getValidNextStatuses,
    canRoleTransition,
    getAvailableTransitionsForRole,
    getResponsibleRole,
    getPhaseForStatus,
    isHandoffTransition,
    getTransitionDescription,
    VALID_TRANSITIONS,
    TRANSITION_PERMISSIONS
  };

  return (
    <ClinicDataContext.Provider value={value}>
      {children}
    </ClinicDataContext.Provider>
  );
};

export default ClinicDataContext;
